/**
 * Scadenziario / Estratto Conto — visibile a admin, agent (filtrato ai
 * suoi clienti), warehouse (read+pagamenti), customer (solo i propri).
 *
 * Mostra:
 *   - 4 KPI cards (aperti, scaduti, parziali, incassato)
 *   - filtri per status / cliente / agente / periodo
 *   - tabella documenti con status colorato
 *   - dialog "Registra incasso" (admin + warehouse)
 *   - export PDF "Estratto Conto" per agente / per cliente / globale
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Receipt, Calendar, AlertTriangle, CheckCircle2, Banknote, FileDown, X, Upload, Users, List, History, Link2, FileSpreadsheet, TrendingUp, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import NumberInput from "../components/NumberInput";
import { API } from "../utils/format";
import { formatMoney } from "../utils/currency";
import TeamsystemImportDialog from "../components/TeamsystemImportDialog";
import StatementsByCustomer, { PaymentDatesCell } from "../components/StatementsByCustomer";
import ReconciliationPanel from "../components/ReconciliationPanel";
import SendDocEmailDialog from "../components/SendDocEmailDialog";
import { shareFileNative } from "../utils/share";

const STATUS_LABELS = {
  open: "Aperto",
  partial: "Parziale",
  paid: "Pagato",
  overdue: "Scaduto",
};

const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-800 border-blue-200",
  partial: "bg-yellow-100 text-yellow-800 border-yellow-200",
  paid: "bg-green-100 text-green-800 border-green-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
};

const fmtDate = (iso) => iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("it-IT") : "—";

const StatementsPage = ({ currentUser, company }) => {
  const role = currentUser?.role || "admin";
  const canRecordPayment = role === "admin" || role === "warehouse";
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({ status: "all", customer_id: "", agent_id: "", date_from: "", date_to: "" });
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [paying, setPaying] = useState(null); // doc currently being paid
  const [payment, setPayment] = useState({ amount: 0, method: "bonifico", note: "", payment_date: new Date().toISOString().slice(0, 10) });
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [viewMode, setViewMode] = useState(role === "customer" ? "list" : "customers");

  // Deep-link: ?customer_id=... apre direttamente la situazione di un cliente
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const custId = params.get("customer_id");
    if (custId && role !== "customer") {
      setFilter((f) => ({ ...f, customer_id: custId }));
      setViewMode("list");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [lastImport, setLastImport] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [prospettoTarget, setProspettoTarget] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");

  const downloadProspetto = async (g) => {
    try {
      const r = await axios.get(`${API}/scadenziario/customer/${g.customer_id}/prospetto-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Prospetto_${(g.business_name || "cliente").replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Prospetto PDF scaricato");
    } catch (e) {
      toast.error("Errore nel download del prospetto");
    }
  };

  const shareProspetto = async (g) => {
    try {
      const r = await axios.get(`${API}/scadenziario/customer/${g.customer_id}/prospetto-pdf`, { responseType: "blob" });
      const filename = `Prospetto_${(g.business_name || "cliente").replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
      const blob = new Blob([r.data], { type: "application/pdf" });
      // Nessun testo/url nel share: così su WhatsApp arriva SOLO il PDF, senza link
      await shareFileNative({ blob, filename, title: filename });
    } catch (e) {
      toast.error("Errore nella condivisione del prospetto");
    }
  };

  const loadLastImport = useCallback(async () => {
    if (role !== "admin") return;
    try {
      const r = await axios.get(`${API}/admin/scadenziario/last-import`);
      setLastImport(r.data?.imported_at ? r.data : null);
    } catch (e) { /* silent */ }
  }, [role]);

  useEffect(() => { loadLastImport(); }, [loadLastImport]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status && filter.status !== "all") params.status = filter.status;
      if (filter.customer_id && filter.customer_id !== "all") params.customer_id = filter.customer_id;
      if (filter.agent_id && filter.agent_id !== "all") params.agent_id = filter.agent_id;
      if (filter.date_from) params.date_from = filter.date_from;
      if (filter.date_to) params.date_to = filter.date_to;
      const [list, agg] = await Promise.all([
        axios.get(`${API}/sales-documents`, { params }),
        axios.get(`${API}/sales-documents/aging-summary`, { params: { agent_id: params.agent_id, customer_id: params.customer_id } }),
      ]);
      setDocs(list.data || []);
      setSummary(agg.data || null);
    } catch (e) {
      toast.error("Errore nel caricamento dello scadenziario");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Carica clienti/agenti per i filtri (admin/warehouse vede tutti, agente vede solo i propri)
    (async () => {
      try {
        if (role !== "customer") {
          const c = await axios.get(`${API}/customers`);
          setCustomers(c.data || []);
        }
        if (role === "admin" || role === "warehouse") {
          const a = await axios.get(`${API}/agents`);
          setAgents(a.data || []);
        }
      } catch (e) { /* silent */ }
    })();
  }, [role]);

  const openPay = (doc) => {
    setPaying(doc);
    setPayment({ amount: doc.amount_open, method: "bonifico", note: "", payment_date: new Date().toISOString().slice(0, 10) });
  };
  const closePay = () => { setPaying(null); setPayment({ amount: 0, method: "bonifico", note: "", payment_date: "" }); };

  const submitPayment = async () => {
    if (!paying) return;
    if (!payment.amount || payment.amount <= 0) { toast.error("Importo non valido"); return; }
    try {
      await axios.post(`${API}/sales-documents/${paying.id}/payments`, payment);
      toast.success("Incasso registrato");
      closePay();
      load();
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-5" data-testid="statements-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#8A1C2A]" /> Scadenziario / Estratto Conto
          </h1>
          <p className="text-sm text-stone-600 mt-1">
            Fatture fiscali importate dall'estratto TeamSystem. Gli ordini spediti (documenti non
            fiscali) non compaiono qui: li trovi nella sezione <b>Documenti</b>.
          </p>
        </div>
        {role === "admin" && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Rimuovere le scadenze duplicate (stesso movimento importato con numerazioni diverse tra PDF ed Excel, es. note di credito contate doppie)?")) return;
                try {
                  const r = await axios.post(`${API}/admin/maintenance/dedup-scadenze`);
                  const n = r.data?.duplicates_removed || 0;
                  toast.success(n > 0 ? `${n} duplicati rimossi` : "Nessun duplicato trovato");
                  if (n > 0) { load(); setReloadKey((k) => k + 1); }
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore");
                }
              }}
              className="border-stone-300 text-stone-700"
              data-testid="btn-dedup-scadenze"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Correggi duplicati
            </Button>
            <Button
              onClick={() => setImportOpen(true)}
              className="bg-[#C5A059] hover:bg-[#a8894c] text-white"
              data-testid="btn-import-teamsystem"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importa da TeamSystem (Excel/PDF)
            </Button>
          </div>
        )}
      </div>

      {/* Banner ultima importazione (admin) */}
      {role === "admin" && (
        <div className={`rounded-lg border px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm ${lastImport ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="last-import-banner">
          <History className="w-4 h-4 flex-shrink-0 opacity-70" />
          {lastImport ? (
            <>
              <span>
                Ultima importazione: <b>{new Date(lastImport.imported_at).toLocaleDateString("it-IT")} {new Date(lastImport.imported_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</b>
              </span>
              {lastImport.statement_date && (
                <span>Situazione al: <b>{fmtDate(lastImport.statement_date)}</b></span>
              )}
              {lastImport.agent_name && <span>Agente: <b>{lastImport.agent_name}</b></span>}
              {lastImport.statement_date && (
                <span className="text-xs opacity-80">→ per aggiornare, esporta da TeamSystem le scadenze dal <b>{fmtDate(lastImport.statement_date)}</b> in poi</span>
              )}
            </>
          ) : (
            <span>Nessuna importazione effettuata: carica il PDF &quot;Situazione scadenze analitica&quot; da TeamSystem per popolare lo scadenziario.</span>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Fatturato complessivo"
          value={formatMoney(summary?.total_invoiced || 0, company)}
          count={summary?.counts?.total || 0}
          countLabel={summary?.counts?.fatture != null ? `${summary.counts.fatture} fatture · ${summary.counts.nc} note credito` : undefined}
          sub={summary?.total_fatture != null
            ? `Fatture ${formatMoney(summary.total_fatture, company)} − NC ${formatMoney(Math.abs(summary.total_nc || 0), company)}${Math.abs(summary.total_other || 0) > 0.005 ? ` · esclusi acconti/riporti/rettifiche: ${formatMoney(summary.total_other, company)}` : ""}`
            : null}
          className="bg-stone-100 border-stone-300 text-stone-900"
          testId="kpi-invoiced"
        />
        <KpiCard icon={<Banknote className="w-5 h-5" />} label="Aperto" value={formatMoney(summary?.total_open || 0, company)} count={summary?.counts?.open || 0} className="bg-blue-50 border-blue-200 text-blue-900" testId="kpi-open" />
        <KpiCard icon={<AlertTriangle className="w-5 h-5" />} label="Scaduto" value={formatMoney(summary?.total_overdue || 0, company)} count={summary?.counts?.overdue || 0} className="bg-red-50 border-red-200 text-red-900" testId="kpi-overdue" />
        <KpiCard icon={<Calendar className="w-5 h-5" />} label="Parziale" value="" count={summary?.counts?.partial || 0} className="bg-yellow-50 border-yellow-200 text-yellow-900" testId="kpi-partial" />
        <KpiCard icon={<CheckCircle2 className="w-5 h-5" />} label="Incassato" value={formatMoney(summary?.total_paid || 0, company)} count={summary?.counts?.paid || 0} className="bg-green-50 border-green-200 text-green-900" testId="kpi-paid" />
      </div>

      {/* Filters */}
      <Card className="p-3 bg-white border-stone-200 flex flex-wrap items-end gap-3" data-testid="statements-filters">
        <div>
          <Label className="text-xs text-stone-600">Stato</Label>
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger className="w-36 bg-white border-stone-300" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="open">Aperti</SelectItem>
              <SelectItem value="overdue">Scaduti</SelectItem>
              <SelectItem value="partial">Parziali</SelectItem>
              <SelectItem value="paid">Pagati</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {customers.length > 0 && (
          <div>
            <Label className="text-xs text-stone-600">Cliente</Label>
            <Select value={filter.customer_id || "all"} onValueChange={(v) => setFilter({ ...filter, customer_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-56 bg-white border-stone-300" data-testid="filter-customer"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {agents.length > 0 && (
          <div>
            <Label className="text-xs text-stone-600">Agente</Label>
            <Select value={filter.agent_id || "all"} onValueChange={(v) => setFilter({ ...filter, agent_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-48 bg-white border-stone-300" data-testid="filter-agent"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli agenti</SelectItem>
                {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs text-stone-600">Da</Label>
          <Input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} className="w-40" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-xs text-stone-600">A</Label>
          <Input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} className="w-40" data-testid="filter-date-to" />
        </div>
        <Button variant="outline" onClick={() => setFilter({ status: "all", customer_id: "", agent_id: "", date_from: "", date_to: "" })}>
          <X className="w-4 h-4 mr-1" /> Reset
        </Button>
        <Button variant="outline" onClick={() => exportExcel(filter)} className="ml-auto border-green-300 text-green-800 hover:bg-green-50" data-testid="btn-export-excel">
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Esporta Excel
        </Button>
        <Button variant="outline" onClick={() => exportStatement(filter, viewMode === "customers" ? customerSearch : "")} data-testid="btn-export-pdf">
          <FileDown className="w-4 h-4 mr-1" /> Esporta PDF
        </Button>
      </Card>

      {/* Toggle vista */}
      {role !== "customer" && (
        <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setViewMode("customers")}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${viewMode === "customers" ? "bg-white shadow-sm font-semibold text-stone-900" : "text-stone-500 hover:text-stone-800"}`}
            data-testid="view-toggle-customers"
          >
            <Users className="w-4 h-4" /> Per cliente
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${viewMode === "list" ? "bg-white shadow-sm font-semibold text-stone-900" : "text-stone-500 hover:text-stone-800"}`}
            data-testid="view-toggle-list"
          >
            <List className="w-4 h-4" /> Tutte le scadenze
          </button>
          {role === "admin" && (
            <button
              onClick={() => setViewMode("reconciliation")}
              className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${viewMode === "reconciliation" ? "bg-white shadow-sm font-semibold text-stone-900" : "text-stone-500 hover:text-stone-800"}`}
              data-testid="view-toggle-reconciliation"
            >
              <Link2 className="w-4 h-4" /> Riconciliazione ordini
            </button>
          )}
        </div>
      )}

      {/* Riconciliazione fatture ↔ ordini (solo admin) */}
      {viewMode === "reconciliation" && role === "admin" && (
        <ReconciliationPanel company={company} reloadKey={reloadKey} onChanged={() => { load(); }} />
      )}

      {/* Vista raggruppata per cliente */}
      {viewMode === "customers" && role !== "customer" && (
        <StatementsByCustomer
          company={company}
          canRecordPayment={canRecordPayment}
          onPay={openPay}
          agentId={filter.agent_id}
          reloadKey={reloadKey}
          onSearchChange={setCustomerSearch}
          onProspetto={role === "agent" || role === "admin" ? downloadProspetto : undefined}
          onShareProspetto={role === "agent" || role === "admin" ? shareProspetto : undefined}
          onSendProspetto={role === "agent" || role === "admin" ? ((g) => setProspettoTarget(g)) : undefined}
        />
      )}

      {/* Invia prospetto situazione contabile via email */}
      <SendDocEmailDialog
        open={!!prospettoTarget}
        onClose={() => setProspettoTarget(null)}
        title={`Invia prospetto — ${prospettoTarget?.business_name || ""}`}
        attachmentLabel={prospettoTarget ? `Prospetto situazione contabile ${prospettoTarget.business_name} (PDF)` : ""}
        endpoint={prospettoTarget ? `${API}/scadenziario/customer/${prospettoTarget.customer_id}/send-prospetto` : ""}
        subjectDefault={prospettoTarget ? `Situazione contabile — ${prospettoTarget.business_name}` : ""}
        bodyDefault={"Gentile cliente,\n\nin allegato il prospetto aggiornato della sua situazione contabile con il dettaglio delle scadenze aperte.\nRestiamo a disposizione per qualsiasi chiarimento.\n\nCordiali saluti"}
        presetEmails={prospettoTarget ? [customers.find((c) => c.id === prospettoTarget.customer_id)?.email].filter(Boolean) : []}
        settingsPath={role === "agent" ? "/agent/settings" : "/admin?tab=settings"}
      />

      {/* Table */}
      {(viewMode === "list" || role === "customer") && (
      <Card className="p-0 overflow-hidden bg-white border-stone-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-left text-xs uppercase text-stone-600">
              <tr>
                <th className="p-3">Numero</th>
                <th className="p-3">N° Ordine</th>
                <th className="p-3">Data Doc.</th>
                <th className="p-3">Scadenza</th>
                <th className="p-3" title="Data dell'ultimo pagamento registrato (clic per vedere i pagamenti parziali)">Pagato il</th>
                <th className="p-3">Cliente</th>
                <th className="p-3 text-right">Importo</th>
                <th className="p-3 text-right">Incassato</th>
                <th className="p-3 text-right">Aperto</th>
                <th className="p-3">Stato</th>
                {canRecordPayment && <th className="p-3 text-right">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={canRecordPayment ? 11 : 10} className="p-6 text-center text-stone-500">Caricamento...</td></tr>}
              {!loading && docs.length === 0 && <tr><td colSpan={canRecordPayment ? 11 : 10} className="p-8 text-center text-stone-500 italic">Nessun documento</td></tr>}
              {!loading && docs.map(d => {
                const isOverdue = d.is_overdue;
                const statusKey = isOverdue ? "overdue" : d.status;
                return (
                  <tr key={d.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`statement-row-${d.doc_number}`}>
                    <td className="p-3 font-mono text-stone-900">{d.doc_number}</td>
                    <td className="p-3 font-mono text-xs text-stone-700">{d.order_number || <span className="text-stone-400 italic">—</span>}</td>
                    <td className="p-3 text-stone-700">{fmtDate(d.doc_date)}</td>
                    <td className={`p-3 ${isOverdue ? "text-red-700 font-semibold" : "text-stone-700"}`}>
                      {fmtDate(d.due_date)}
                      {d.days_to_due !== null && d.status !== "paid" && (
                        <div className="text-[10px] text-stone-500">
                          {d.days_to_due >= 0 ? `tra ${d.days_to_due} gg` : `${Math.abs(d.days_to_due)} gg fa`}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-stone-700" data-testid={`statement-regdate-${d.doc_number}`}>
                      <PaymentDatesCell doc={d} />
                    </td>
                    <td className="p-3 text-stone-900">{d.customer_snapshot?.business_name || "—"}</td>
                    <td className="p-3 text-right">{formatMoney(d.amount_total, company)}</td>
                    <td className="p-3 text-right text-green-700">{formatMoney(d.amount_paid, company)}</td>
                    <td className="p-3 text-right font-semibold">{formatMoney(d.amount_open, company)}</td>
                    <td className="p-3">
                      <Badge className={`${STATUS_COLORS[statusKey]} border`}>{STATUS_LABELS[statusKey]}</Badge>
                    </td>
                    {canRecordPayment && (
                      <td className="p-3 text-right">
                        {d.status !== "paid" && (
                          <Button size="sm" onClick={() => openPay(d)} className="bg-[#8A1C2A] text-white" data-testid={`record-payment-${d.doc_number}`}>
                            <Banknote className="w-3 h-3 mr-1" /> Incassa
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={!!paying} onOpenChange={(o) => !o && closePay()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registra incasso — {paying?.doc_number}</DialogTitle>
          </DialogHeader>
          {paying && (
            <div className="space-y-3">
              <div className="p-3 bg-stone-50 rounded text-sm">
                <div className="text-stone-600">Cliente: <strong className="text-stone-900">{paying.customer_snapshot?.business_name}</strong></div>
                <div className="text-stone-600">Aperto: <strong className="text-[#8A1C2A]">{formatMoney(paying.amount_open, company)}</strong></div>
              </div>
              <div>
                <Label>Importo {company?.currency_symbol || "€"}</Label>
                <NumberInput value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: parseFloat(e.target.value) || 0 })} data-testid="payment-amount-input" />
              </div>
              <div>
                <Label>Data incasso</Label>
                <Input type="date" value={payment.payment_date} onChange={(e) => setPayment({ ...payment, payment_date: e.target.value })} />
              </div>
              <div>
                <Label>Metodo</Label>
                <Select value={payment.method} onValueChange={(v) => setPayment({ ...payment, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="assegno">Assegno</SelectItem>
                    <SelectItem value="carta">Carta</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Input value={payment.note} onChange={(e) => setPayment({ ...payment, note: e.target.value })} placeholder="Riferimento bonifico, n. assegno..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePay}>Annulla</Button>
            <Button onClick={submitPayment} className="bg-[#8A1C2A] text-white" data-testid="confirm-payment-btn">Registra incasso</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TeamSystem PDF import — admin only */}
      {role === "admin" && (
        <TeamsystemImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={() => { load(); loadLastImport(); setReloadKey((k) => k + 1); }}
          customers={customers}
          agents={agents}
        />
      )}
    </div>
  );
};

const KpiCard = ({ icon, label, value, count, countLabel, sub, className, testId }) => (
  <Card className={`p-4 border ${className}`} data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
        <p className="text-xl font-bold mt-1">{value || "—"}</p>
      </div>
      <div className="opacity-60">{icon}</div>
    </div>
    <p className="text-[10px] mt-1 opacity-70">{countLabel || `${count} documenti`}</p>
    {sub && <p className="text-[10px] mt-0.5 opacity-60 leading-tight">{sub}</p>}
  </Card>
);

/**
 * Export Excel dello scadenziario con i filtri correnti (stesso tracciato TeamSystem).
 */
const exportExcel = async (filter) => {
  try {
    const params = {};
    if (filter.status && filter.status !== "all") params.status = filter.status;
    if (filter.customer_id && filter.customer_id !== "all") params.customer_id = filter.customer_id;
    if (filter.agent_id && filter.agent_id !== "all") params.agent_id = filter.agent_id;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    const r = await axios.get(`${API}/scadenziario/export-excel`, { params, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Scadenziario_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success("Excel scadenziario scaricato");
  } catch (e) {
    toast.error("Errore nell'esportazione Excel");
  }
};

/**
 * Export PDF dello scadenziario (server-side): diviso per agente › cliente › scadenze,
 * con date pagamenti e totali — rispetta i filtri correnti e la ricerca della vista "Per cliente".
 */
const exportStatement = async (filter, search) => {
  const tid = toast.loading("Generazione PDF scadenziario in corso…");
  try {
    const params = {};
    if (filter.status && filter.status !== "all") params.status = filter.status;
    if (filter.customer_id && filter.customer_id !== "all") params.customer_id = filter.customer_id;
    if (filter.agent_id && filter.agent_id !== "all") params.agent_id = filter.agent_id;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    if (search && search.trim()) params.q = search.trim();
    const r = await axios.get(`${API}/scadenziario/export-pdf`, { params, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Scadenziario_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success("PDF scadenziario scaricato", { id: tid });
  } catch (e) {
    toast.error("Errore nell'esportazione PDF", { id: tid });
  }
};

export default StatementsPage;
